import { CodingExamExaminerData } from '../../../../../../../store/workspace/types';
import { examStatusTitle } from '../../../../../../../utils/generalHelpers';
import CheckBox from '../../../../../../../components/CheckBox';
import { ForwardedRef, forwardRef, Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CODING_QUESTION_INDEXES } from '../../../../constants';
import { useApplicantSelection } from '../../../../hooks/useApplicantSelection';
import { useExpandableRows } from '../../../../hooks/useExpandableRows';
import { useExaminerProfile } from '../../../../hooks/useExaminerProfile';
import { codingScoreOf, CODING_METRIC_GROUPS } from '../../../../codingMetrics';
import styles from './style.module.scss';
import {
    DangerIcon,
    ProfileIcon,
    DocumentIcon,
    ChevronUpIcon,
    TimesCircleIcon,
    ChevronDownIcon,
    CheckCircleIcon,
} from '../../../../../../../assets/icons';

/** Metric columns of a question row, in table order. */
const SCORE_COLUMNS = CODING_METRIC_GROUPS.flatMap(group => group.metrics);

type SortDirection = 'ascend' | 'descend';

interface Props {
    selectable: boolean;
    jobId: string | undefined;
    examinersInfo: CodingExamExaminerData[];
};

const CodingExamTable = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const { jobId, selectable, examinersInfo } = props;

    const navigate = useNavigate();
    const openExaminerProfile = useExaminerProfile(jobId);
    const { isExpanded, toggleRow } = useExpandableRows<string>();
    const [direction, setDirection] = useState<SortDirection>('descend');

    const tableInfo = useMemo(() => [...examinersInfo].sort((a, b) => direction === 'ascend'
        ? a.grade - b.grade
        : b.grade - a.grade), [examinersInfo, direction]);

    const { isSelected, toggleApplicant, toggleAll, allSelected } = useApplicantSelection(tableInfo);

    /**
     * Each applicant occupies three rows — one per coding question. The name,
     * status and grade cells of the first row span all three; the row actions
     * are anchored to the middle row so they end up vertically centred.
     */
    const renderQuestionRow = (
        applicant: CodingExamExaminerData,
        questionIndex: number,
        striped: boolean,
        index: number,
    ) => {
        const score = applicant.scores[questionIndex];

        return (
            <tr style={{ backgroundColor: striped ? '#F1F5F9' : 'transparent' }}>
                {questionIndex === 0 && <>
                    <td rowSpan={3}>{index + 1}</td>
                    <td rowSpan={3}>
                        <div className={styles.examinerName}>
                            {selectable && <CheckBox
                                onClick={() => toggleApplicant(applicant.userId)}
                                checked={isSelected(applicant.userId)}
                            />}
                            <span>{applicant.first_name} {applicant.family_name}</span>
                            {applicant.proctoring && <div className={styles.proctoring}>
                                <DangerIcon />
                                <span className={styles.proctoring_tooltip}>
                                    {applicant.first_name} {applicant.family_name} was caught cheating in this exam!
                                </span>
                            </div>}
                        </div>
                    </td>
                    <td rowSpan={3}>
                        <span className={styles.status} data-status={applicant.status}>
                            {examStatusTitle(applicant.status)}
                        </span>
                    </td>
                    <td rowSpan={3}>
                        <div className={styles.grade}>
                            <span className={styles.language}>{applicant.language}</span>
                            <span>{applicant.grade}/10</span>
                        </div>
                    </td>
                </>}

                <td>Q{questionIndex + 1}</td>
                {SCORE_COLUMNS.map(metric => <td key={metric.key}>
                    {codingScoreOf(score, metric.key)}/10
                </td>)}
                <td>{score?.verifies_constraints ? <CheckCircleIcon /> : <TimesCircleIcon />}</td>

                {questionIndex === 1 ? <>
                    <td data-noprint='true'><DocumentIcon onClick={() => navigate(`${applicant.userId}`)} /></td>
                    <td data-noprint='true'><ProfileIcon onClick={() => openExaminerProfile(applicant.userId)} /></td>
                </> : <>
                    <td></td>
                    <td></td>
                </>}
            </tr>
        );
    };

    return (
        <div className={styles.table} ref={ref}>
            <table>
                <thead>
                    <tr>
                        <th colSpan={3} data-no-border='true'>
                            {selectable && <div className={styles.select_all}>
                                <CheckBox onClick={toggleAll} checked={allSelected} />
                                <span>Select All ({tableInfo.length})</span>
                            </div>}
                        </th>
                        <th data-no-border='true'></th>
                        <th colSpan={2} data-group-start='test-cases'>
                            <span className={styles.head_section}>Test Cases</span>
                        </th>
                        <th colSpan={2} data-group-middle='complexity'>
                            <span className={styles.head_section}>Complexity</span>
                        </th>
                        <th colSpan={5} data-group-end='code-quality'>
                            <span className={styles.head_section}>Code Quality</span>
                        </th>
                    </tr>
                    <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>
                            <div className={styles.head_group}>
                                <div className={styles.head_group_sort}>
                                    <ChevronUpIcon onClick={() => setDirection('descend')} />
                                    <ChevronDownIcon onClick={() => setDirection('ascend')} />
                                </div>
                                <span>Grade</span>
                            </div>
                        </th>
                        <th>Q</th>
                        {SCORE_COLUMNS.map(metric => <th key={metric.key}>{metric.title}</th>)}
                        <th>Verification<br />constraint</th>
                        <th data-noprint='true'>Sheet &<br />feedback</th>
                        <th data-noprint='true'>Info</th>
                    </tr>
                </thead>
                <tbody>
                    {tableInfo.length === 0
                        ? <tr><td colSpan={14}>No data.</td></tr>
                        : tableInfo.map((applicant, index) => <Fragment key={applicant.userId}>
                            {CODING_QUESTION_INDEXES.map(questionIndex => <Fragment key={questionIndex}>
                                {renderQuestionRow(applicant, questionIndex, index % 2 === 1, index)}
                            </Fragment>)}
                        </Fragment>)}
                </tbody>
            </table>

            <div className={styles.mobile_list}>
                {tableInfo.map(applicant => <div className={styles.card} key={applicant.userId}>
                    <div className={styles.card_header} onClick={() => toggleRow(applicant.userId)}>
                        <span>{applicant.first_name} {applicant.family_name}</span>
                        <ChevronDownIcon style={{ transform: isExpanded(applicant.userId) ? 'rotate(180deg)' : 'none' }} />
                    </div>

                    {isExpanded(applicant.userId) && <div className={styles.card_body}>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Status</span>
                                <span className={styles.status} data-status={applicant.status}>
                                    {examStatusTitle(applicant.status)}
                                </span>
                            </div>
                        </div>
                        <div className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>Grade</span>
                                <span>{applicant.grade}/10</span>
                            </div>
                        </div>

                        {CODING_METRIC_GROUPS.map(group => <div key={group.title} className={styles.card_body_section}>
                            <div className={styles.card_body_section_title}>
                                <span>{group.title}</span>
                            </div>
                            <div className={styles.card_body_section_content}>
                                {CODING_QUESTION_INDEXES.map(questionIndex => <div
                                    key={questionIndex}
                                    className={styles.card_body_section_content_group}
                                >
                                    <div className={styles.card_body_section_content_group_title}>
                                        <span>Q{questionIndex + 1}</span>
                                    </div>
                                    <div className={styles.card_body_section_content_group_details}>
                                        {group.metrics.map(metric => <div
                                            key={metric.key}
                                            className={styles.card_body_section_content_group_details_item}
                                        >
                                            <span>{metric.title}</span>
                                            <span>{codingScoreOf(applicant.scores[questionIndex], metric.key)}/10</span>
                                        </div>)}
                                    </div>
                                </div>)}
                            </div>
                        </div>)}
                    </div>}
                </div>)}
            </div>
        </div>
    );
});

export default CodingExamTable;
